'use client'

import type { Data } from '@repo/strapi-types'
import { ChevronDown } from '@/components/icons/ChevronDown'
import { FeatureCard } from '@/components/page-builder/global/featureRoadmap/FeatureCard'
import {
	getGridColumnClass,
	getGridRowClass,
} from '@/components/page-builder/global/featureRoadmap/grid-helper'
import { useCollapsibleCategories } from '@/components/page-builder/global/featureRoadmap/useCollapsibleCategories'
import Heading from '@/components/typography/Heading'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/styles'

type FeatureRoadmapDesktopProps = {
	readonly categories: Data.Component<'shared.roadmap-category'>[]
	readonly primaryColumnTitle: string
	readonly secondaryColumnTitle: string
}

export function FeatureRoadmapDesktop({
	categories,
	primaryColumnTitle,
	secondaryColumnTitle,
}: FeatureRoadmapDesktopProps) {
	const { openCategories, toggleCategory } = useCollapsibleCategories(categories)

	return (
		<div className="hidden lg:block bg-white rounded-xl">
			{/* Column Headers */}
			<div className="grid grid-cols-2 gap-14 mb-12 px-0">
				<div className="w-fit lok-text-display-small text-brand-black">{primaryColumnTitle}</div>
				<div className="w-fit lok-text-display-small text-brand-black">{secondaryColumnTitle}</div>
			</div>

			{/* Categories */}
			<div className="flex flex-col gap-12">
				{categories.map((category) => {
					if (!category || !category.id) return null

					const categoryId = category.id
					const isOpen = openCategories[categoryId] ?? false
					const hasItems =
						(category.primaryColumnItems && category.primaryColumnItems.length > 0) ||
						(category.secondaryColumnItems && category.secondaryColumnItems.length > 0)

					if (!hasItems) return null

					return (
						<Collapsible
							key={categoryId}
							open={isOpen}
							onOpenChange={() => toggleCategory(categoryId)}
							className="w-full"
						>
							{/* Category Header */}
							<CollapsibleTrigger className="group inline-flex items-center gap-2 cursor-pointer py-5 hover:opacity-70 transition-opacity">
								<Heading tag="h2" fontSize="headingSmall">
									{category.title}
								</Heading>
								<ChevronDown
									className={cn('size-5 transition-transform duration-200', isOpen && 'rotate-180')}
								/>
							</CollapsibleTrigger>

							{/* Category Content */}
							<CollapsibleContent>
								<div className="grid grid-cols-[1fr_1fr_calc(var(--spacing)*3.5)_1fr_1fr] gap-5">
									{/* Primary Column Items */}
									{category.primaryColumnItems?.map((item, index) =>
										item ? (
											<div
												key={item.id}
												className={cn(getGridColumnClass(index, 'primary'), getGridRowClass(index))}
											>
												<FeatureCard item={item} />
											</div>
										) : null,
									)}

									{/* Secondary Column Items */}
									{category.secondaryColumnItems?.map((item, index) =>
										item ? (
											<div
												key={item.id}
												className={cn(
													getGridColumnClass(index, 'secondary'),
													getGridRowClass(index),
												)}
											>
												<FeatureCard item={item} variant="secondary" />
											</div>
										) : null,
									)}
								</div>
							</CollapsibleContent>
						</Collapsible>
					)
				})}
			</div>
		</div>
	)
}
